/* eslint-disable react-refresh/only-export-components */
import { type OperationVariables } from '@apollo/client';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { pollExportFile } from '../utils/poll-export-file';
import { type UseExportFileProps } from '../hooks/useExportFile';

type BackgroundExportContextValue = {
  startExport: <
    TMutation,
    TMutationVars extends OperationVariables,
    TQuery,
    TQueryVars extends OperationVariables,
  >(
    config: UseExportFileProps<TMutation, TMutationVars, TQuery, TQueryVars>,
    mutationVariables: TMutationVars,
  ) => void;
  /** True while any export job is in progress — survives navigation. */
  isExporting: boolean;
};

export const BackgroundExportContext =
  createContext<BackgroundExportContextValue | null>(null);

export function BackgroundExportFileProvider({
  children,
}: {
  children: ReactNode;
}) {
  const activeJobs = useRef<Map<string, AbortController>>(new Map());
  const [activeJobCount, setActiveJobCount] = useState(0);

  const startExport = useCallback(
    async <
      TMutation,
      TMutationVars extends OperationVariables,
      TQuery,
      TQueryVars extends OperationVariables,
    >(
      config: UseExportFileProps<TMutation, TMutationVars, TQuery, TQueryVars>,
      mutationVariables: TMutationVars,
    ) => {
      const jobId = crypto.randomUUID();
      const abortController = new AbortController();
      activeJobs.current.set(jobId, abortController);
      setActiveJobCount(c => c + 1);

      const toastId = toast.loading(
        'Getting your file ready. This may take approximately 1 minute.',
      );

      config.events?.onStartExport?.();

      try {
        const exportMutationResult = await config.clientInstance.mutate({
          mutation: config.exportMutationDocument,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          variables: mutationVariables as any,
          fetchPolicy: config.fetchPolicy ?? 'network-only',
          context: config.context,
        });

        if (exportMutationResult.errors?.length) {
          throw new Error(exportMutationResult.errors[0].message);
        }

        const mutationData = exportMutationResult.data as TMutation;
        config.events?.onExportSuccess?.(mutationData);

        const queryVariables =
          config.extractExportDataForQueryVariables(mutationData);

        await pollExportFile<TQuery, typeof queryVariables>({
          client: config.clientInstance,
          queryDocument: config.getExportFileQueryDocument,
          variables: queryVariables,
          extractGetFileData: config.extractGetFileData as (
            data: TQuery,
          ) => ReturnType<typeof config.extractGetFileData>,
          maxRetries: config.maxRetries ?? 20,
          delayTimeBetweenAttempts: config.delayTimeBetweenAttempts ?? 2000,
          useExponentialBackoff: config.useExponentialBackoff ?? true,
          maxBackoffDelay: config.maxBackoffDelay ?? 30000,
          signal: abortController.signal,
          onCompleted: queryData => {
            toast.success('Your file is ready!', { id: toastId });
            config.events?.onGetFileSuccess?.(queryData);
          },
          onFailed: error => {
            toast.dismiss(toastId);
            config.events?.onGetFileFailed?.(error);
          },
        });
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Export failed. Please try again.');
        toast.dismiss(toastId);
        config.events?.onExportFailed?.(error);
      } finally {
        activeJobs.current.delete(jobId);
        setActiveJobCount(c => c - 1);
      }
      // activeJobs is a ref (stable); no other deps needed
    },
    [],
  );

  const contextValue = useMemo(
    () => ({ startExport, isExporting: activeJobCount > 0 }),
    [startExport, activeJobCount],
  );

  return (
    <BackgroundExportContext.Provider value={contextValue}>
      {children}
    </BackgroundExportContext.Provider>
  );
}

export function useBackgroundExportContext() {
  const ctx = useContext(BackgroundExportContext);
  if (!ctx) {
    throw new Error(
      'useBackgroundExportContext must be used within BackgroundExportFileProvider',
    );
  }
  return ctx;
}
